import React from "react";
import NavBar from "../../components/CustomNav/CustomNav.Component";

export default function ParentTermsOfUse() {
    return (
        <div
            style={{
                background: "#0b0f14",
                color: "#eaf2ff",
                minHeight: "100vh",
                fontFamily: "Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
                lineHeight: 1.7,
            }}
        >
            <NavBar />

            <div style={{ maxWidth: 980, margin: "24px auto" }}>
                <h1 style={{ color: "#acff24", marginBottom: 8 }}>
                    Performance Learning Platform Terms of Use (Parents)
                </h1>
                <p style={{ color: "#3deaf7", marginTop: 0 }}>Last updated: 26th February 2026</p>

                <p>
                    These Terms of Use (&quot;Terms&quot;) govern your purchase and use of the Performance
                    Learning digital platform, including the Foresight Assessment and Soar Online Learning.
                    Please read these Terms carefully before purchasing. By completing your online checkout
                    and creating an account, you confirm that you have read, understood and agree to be bound
                    by these Terms.
                </p>

                <Section title="1. Who We Are">
                    <p>
                        We are PL Education Ltd, trading as Performance Learning, a company registered in
                        England and Wales under company number 06687297. Our registered office is at 42 Upper
                        Berkeley Street, London, England, W1H 5QL.
                    </p>
                    <p>In these Terms:</p>
                    <ul>
                        <li>&quot;We&quot;, &quot;us&quot;, &quot;our&quot; means PL Education Ltd trading as Performance Learning.</li>
                        <li>&quot;You&quot; means the parent or legal guardian purchasing access to the Platform.</li>
                        <li>&quot;Child&quot; means the learner for whom access is purchased.</li>
                        <li>
                            &quot;Platform&quot; means the Performance Learning digital platform, including Foresight and
                            Soar.
                        </li>
                    </ul>
                </Section>

                <Section title="2. The Services">
                    <h3 style={{ color: "#3deaf7", fontSize: 18, textTransform: "initial" }}>2.1 Foresight Assessment</h3>
                    <p>
                        Foresight is a paid, online educational assessment designed to provide insight into
                        areas that may influence academic performance and wellbeing, including (but not limited
                        to):
                    </p>
                    <ul>
                        <li>Sleep</li>
                        <li>Time management</li>
                        <li>Memory</li>
                        <li>Organisation</li>
                        <li>Study habits</li>
                    </ul>
                    <p>Foresight provides educational insight only. It:</p>
                    <ol>
                        <li>Is not a medical tool.</li>
                        <li>Is not a diagnostic assessment.</li>
                        <li>Does not provide therapeutic or clinical advice.</li>
                        <li>Is not prescriptive.</li>
                    </ol>
                    <p>
                        It is designed solely to support understanding of learning behaviours and academic
                        development.
                    </p>

                    <h3 style={{ color: "#3deaf7", fontSize: 18, textTransform: "initial" }}>2.2 Soar Online Learning (Optional Additional Package)</h3>
                    <p>
                        If purchased, Soar provides digital learning pathways tailored to areas identified
                        through the Child&apos;s Foresight results.
                    </p>
                    <p>Soar:</p>
                    <ol>
                        <li>Is an educational skills development programme.</li>
                        <li>Is self-directed unless otherwise specified.</li>
                        <li>Does not constitute tutoring, therapy, counselling or medical support.</li>
                    </ol>
                    <p>
                        Access to Soar is only provided where the additional package has been purchased.
                    </p>
                </Section>

                <Section title="3. Eligibility and Parental Authority">
                    <p>By purchasing access to the Platform, You confirm that:</p>
                    <ol>
                        <li>You are the Child&apos;s parent or legal guardian.</li>
                        <li>You have full legal authority to enter into this agreement on the Child&apos;s behalf.</li>
                        <li>You consent to the Child using the Platform.</li>
                        <li>
                            You consent to the processing of the Child&apos;s personal data in accordance with our
                            Privacy Policy.
                        </li>
                    </ol>
                    <p>
                        We reserve the right to request reasonable confirmation of parental authority if
                        required.
                    </p>
                </Section>

                <Section title="4. Account Registration and Security">
                    <ol>
                        <li>
                            You must provide accurate, complete and up-to-date information when creating an
                            account.
                        </li>
                        <li>You are responsible for maintaining the confidentiality of login details.</li>
                        <li>You must ensure that your Child uses the Platform in accordance with these Terms.</li>
                        <li>
                            You must notify us immediately if you suspect unauthorised use of the account.
                        </li>
                    </ol>
                    <p>You remain responsible for all activity carried out under your account.</p>
                </Section>

                <Section title="5. Payment">
                    <ol>
                        <li>All purchases are made directly online at checkout.</li>
                        <li>Prices are displayed in GBP and include VAT where applicable.</li>
                        <li>Payment must be made in full at the time of purchase.</li>
                        <li>
                            Access to Foresight and/or Soar will not be granted until payment has been
                            successfully processed.
                        </li>
                    </ol>
                    <p>
                        If Soar is purchased as an additional package, this will be clearly indicated during
                        checkout.
                    </p>
                    <p>
                        We reserve the right to amend pricing at any time, but changes will not affect
                        purchases already completed.
                    </p>
                </Section>

                <Section title="6. Your Right to Cancel (Consumer Contracts Regulations 2013)">
                    <p>
                        As this is a digital service purchased online, you have a statutory right to cancel
                        within 14 days of purchase.
                    </p>
                    <p>However:</p>
                    <ol>
                        <li>
                            If you request access to Foresight or Soar within the 14-day cancellation period, you
                            expressly agree that the service will begin immediately.
                        </li>
                        <li>
                            Once the Child has accessed the assessment or digital lessons, you acknowledge that
                            you may lose your right to a full refund.
                        </li>
                        <li>
                            If cancellation occurs after partial use, we may deduct an amount proportionate to
                            the services already provided.
                        </li>
                    </ol>
                    <p>
                        To exercise your right to cancel, please contact us at{" "}
                        <span style={{ color: "#3deaf7" }}>uk@myperformancelearning.com</span>
                    </p>
                </Section>

                <Section title="7. Intellectual Property">
                    <ol>
                        <li>
                            All content on the Platform, including assessments, reports, dashboards, lesson
                            materials, branding, software and design, is owned by or licensed to us.
                        </li>
                        <li>
                            Content is provided solely for your Child&apos;s personal, non-commercial educational use.
                        </li>
                        <li>
                            You must not copy, reproduce, distribute, record, modify, share or commercially
                            exploit any materials.
                        </li>
                        <li>
                            Assessment reports must not be altered, misrepresented or used in a misleading
                            manner.
                        </li>
                    </ol>
                    <p>Unauthorised use may result in immediate suspension or termination without refund.</p>
                </Section>

                <Section title="8. Acceptable Use">
                    <p>You agree that neither You nor your Child will:</p>
                    <ol>
                        <li>Use the Platform for any unlawful or harmful purpose.</li>
                        <li>
                            Attempt to copy, reverse engineer, decompile or interfere with the Platform.
                        </li>
                        <li>Share login credentials with third parties.</li>
                        <li>Upload or transmit inappropriate, offensive or unlawful content.</li>
                    </ol>
                    <p>We may suspend or terminate access if these Terms are breached.</p>
                </Section>

                <Section title="9. Educational Disclaimer">
                    <ol>
                        <li>Foresight results are generated based on responses submitted by the Child.</li>
                        <li>Results depend upon honest, accurate and complete responses.</li>
                        <li>Outcomes vary between learners.</li>
                        <li>We do not guarantee academic improvement or specific performance outcomes.</li>
                        <li>
                            The Platform does not replace professional medical, psychological, therapeutic or
                            specialist educational advice.
                        </li>
                    </ol>
                    <p>
                        If you have concerns about your Child&apos;s health, wellbeing or learning needs, you should
                        consult an appropriately qualified professional.
                    </p>
                </Section>

                <Section title="10. Parental Responsibility">
                    <p>You acknowledge that:</p>
                    <ol>
                        <li>Your Child&apos;s engagement, effort and honesty materially affect outcomes.</li>
                        <li>We are not responsible for ensuring lesson completion.</li>
                        <li>You remain responsible for supervising your Child&apos;s use of the Platform.</li>
                        <li>
                            You are responsible for ensuring your Child has appropriate internet access and
                            compatible devices.
                        </li>
                    </ol>
                </Section>

                <Section title="11. Data Protection">
                    <p>We process personal data in accordance with:</p>
                    <ul>
                        <li>The UK General Data Protection Regulation (UK GDPR); and</li>
                        <li>The Data Protection Act 2018.</li>
                    </ul>
                    <p>Full details are set out in our Privacy Policy.</p>
                    <p>By purchasing access, You:</p>
                    <ol>
                        <li>
                            Consent to the processing of your Child&apos;s data for assessment, reporting and service
                            delivery purposes.
                        </li>
                        <li>Confirm that information provided is accurate.</li>
                        <li>
                            Understand that anonymised or aggregated data may be used for research, analysis and
                            service improvement.
                        </li>
                    </ol>
                </Section>

                <Section title="12. Availability of the Platform">
                    <ol>
                        <li>
                            We use reasonable endeavours to ensure that the Platform is available at all times.
                        </li>
                        <li>We do not guarantee uninterrupted or error-free access.</li>
                        <li>
                            Access may be suspended temporarily for maintenance, security updates or technical
                            reasons.
                        </li>
                    </ol>
                    <p>
                        We shall not be liable for temporary interruptions beyond our reasonable control.
                    </p>
                </Section>

                <Section title="13. Limitation of Liability">
                    <p>Nothing in these Terms excludes or limits liability for:</p>
                    <ul>
                        <li>Death or personal injury caused by negligence;</li>
                        <li>Fraud or fraudulent misrepresentation;</li>
                        <li>Any liability that cannot lawfully be excluded under applicable law.</li>
                    </ul>
                    <p>Subject to the above:</p>
                    <ol>
                        <li>We shall not be liable for any indirect or consequential loss.</li>
                        <li>
                            We are not liable for loss of academic opportunity, exam performance or educational
                            outcomes.
                        </li>
                        <li>
                            Our total liability arising from or in connection with the Platform shall not exceed
                            the total amount paid by you for the relevant service.
                        </li>
                    </ol>
                </Section>

                <Section title="14. Suspension and Termination">
                    <p>We may suspend or terminate access if:</p>
                    <ol>
                        <li>These Terms are breached;</li>
                        <li>Payment is disputed, reversed or fraudulent;</li>
                        <li>The Platform is misused;</li>
                        <li>We are required to do so by law.</li>
                    </ol>
                    <p>Upon termination, all rights granted under these Terms shall immediately cease.</p>
                </Section>

                <Section title="15. Changes to These Terms">
                    <p>
                        We may update these Terms from time to time. The latest version will always be
                        available on our website.
                    </p>
                    <p>
                        Continued use of the Platform after updates constitutes acceptance of the revised Terms.
                    </p>
                </Section>

                <Section title="16. Governing Law and Jurisdiction">
                    <p>These Terms are governed by the laws of England and Wales.</p>
                    <p>
                        The courts of England and Wales shall have exclusive jurisdiction to resolve any dispute
                        arising out of or in connection with these Terms.
                    </p>
                </Section>
            </div>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <section style={{ marginTop: 28 }}>
            <h2 style={{ color: "#acff24", marginBottom: 8, fontSize: 24 }}>{title}</h2>
            {children}
        </section>
    );
}


// WEBPACK FOOTER //
// ./src/pages/Onboard/TermsOfUse.tsx